import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AddCatPlusLinesResult,
  Agreement,
  AgreementHoursUsage,
  AgreementLine,
  AgreementOverageAlertSettings,
  AgreementPortalBinding,
  AgreementPortalCaiaInfo,
  AgreementPortalConnectBoosterData,
  AgreementPortalSettlement,
  AgreementServiceCategoryRate,
  AgreementStatus,
  AgreementType,
  BindAgreementToPortalInput,
  CreateAgreementInput,
  CreateAgreementLineInput,
  CreateAgreementServiceCategoryRateInput,
  PaginatedResult,
  QuickAddPayAsYouGoInput,
  SetAgreementSettlementStateInput,
  SortDirection,
  UpdateAgreementOverageRecipientsInput,
  UpdateAgreementInput,
  UpdateAgreementLineInput,
  UpdateAgreementServiceCategoryRateInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

// ---------------------------------------------------------------------------
// Agreements
// ---------------------------------------------------------------------------

export interface AgreementListParams {
  page: number;
  pageSize: number;
  customerId?: string;
  productId?: string;
  status?: AgreementStatus;
  type?: AgreementType;
  /** Agreement-subtype ids; matches any of the listed subtypes. */
  subtypeIds?: string[];
  sortBy?: string;
  sortDir?: SortDirection;
}

const agreementKeys = {
  all: ['agreements'] as const,
  list: (params: AgreementListParams) => ['agreements', 'list', params] as const,
  detail: (id: string) => ['agreements', 'detail', id] as const,
  lines: (agreementId: string) => ['agreements', agreementId, 'lines'] as const,
  rates: (agreementId: string) => ['agreements', agreementId, 'rates'] as const,
  hoursUsage: (agreementId: string, month?: string) =>
    ['agreements', agreementId, 'hours-usage', month ?? 'current'] as const,
  overageAlert: (agreementId: string) => ['agreements', agreementId, 'overage-alert'] as const,
  portalBinding: (agreementId: string) => ['agreements', agreementId, 'portal-binding'] as const,
  portalCaia: (agreementId: string) => ['agreements', agreementId, 'portal-caia'] as const,
  portalSettlement: (agreementId: string, period?: string) =>
    ['agreements', agreementId, 'portal-settlement', period ?? 'default'] as const,
  portalConnectBooster: (agreementId: string) =>
    ['agreements', agreementId, 'portal-connectbooster'] as const,
};

export function useAgreements(
  params: AgreementListParams,
  options?: { enabled?: boolean },
): UseQueryResult<PaginatedResult<Agreement>> {
  return useQuery({
    queryKey: agreementKeys.list(params),
    enabled: options?.enabled ?? true,
    queryFn: () =>
      api.get<PaginatedResult<Agreement>>(
        `/agreements${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          customerId: params.customerId,
          productId: params.productId,
          status: params.status,
          type: params.type,
          subtypeIds: params.subtypeIds?.length ? params.subtypeIds.join(',') : undefined,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useAgreement(id: string): UseQueryResult<Agreement> {
  return useQuery({
    queryKey: agreementKeys.detail(id),
    enabled: Boolean(id),
    queryFn: () => api.get<Agreement>(`/agreements/${id}`),
  });
}

export function useAgreementHoursUsage(
  id: string,
  month?: string,
  options?: { enabled?: boolean },
): UseQueryResult<AgreementHoursUsage> {
  return useQuery({
    queryKey: agreementKeys.hoursUsage(id, month),
    enabled: (options?.enabled ?? true) && Boolean(id),
    queryFn: () =>
      api.get<AgreementHoursUsage>(`/agreements/${id}/hours-usage${buildQuery({ month })}`),
  });
}

// ---------------------------------------------------------------------------
// Catalyst+ overage alert (Cat+ Usage tab)
// ---------------------------------------------------------------------------

export function useAgreementOverageAlert(
  id: string,
  options?: { enabled?: boolean },
): UseQueryResult<AgreementOverageAlertSettings> {
  return useQuery({
    queryKey: agreementKeys.overageAlert(id),
    enabled: (options?.enabled ?? true) && Boolean(id),
    queryFn: () => api.get<AgreementOverageAlertSettings>(`/agreements/${id}/overage-alert`),
  });
}

export function useSetAgreementOverageRecipients(
  id: string,
): UseMutationResult<
  AgreementOverageAlertSettings,
  unknown,
  UpdateAgreementOverageRecipientsInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAgreementOverageRecipientsInput) =>
      api.put<AgreementOverageAlertSettings>(`/agreements/${id}/overage-alert/recipients`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(agreementKeys.overageAlert(id), data);
      // Contact flags changed, so any cached customer contact list is now stale.
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Catalyst Portal billing (Portal Billing tab)
// ---------------------------------------------------------------------------

export function useAgreementPortalBinding(
  id: string,
  options?: { enabled?: boolean },
): UseQueryResult<AgreementPortalBinding> {
  return useQuery({
    queryKey: agreementKeys.portalBinding(id),
    enabled: (options?.enabled ?? true) && Boolean(id),
    queryFn: () => api.get<AgreementPortalBinding>(`/agreements/${id}/portal-billing/binding`),
  });
}

export function useBindAgreementToPortal(
  id: string,
): UseMutationResult<AgreementPortalBinding, unknown, BindAgreementToPortalInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BindAgreementToPortalInput) =>
      api.post<AgreementPortalBinding>(`/agreements/${id}/portal-billing/binding`, input),
    onSuccess: (binding) => {
      queryClient.setQueryData(agreementKeys.portalBinding(id), binding);
      void queryClient.invalidateQueries({ queryKey: agreementKeys.portalBinding(id) });
    },
  });
}

export function useAgreementPortalCaia(
  id: string,
  options?: { enabled?: boolean },
): UseQueryResult<AgreementPortalCaiaInfo> {
  return useQuery({
    queryKey: agreementKeys.portalCaia(id),
    enabled: (options?.enabled ?? true) && Boolean(id),
    queryFn: () => api.get<AgreementPortalCaiaInfo>(`/agreements/${id}/portal-billing/caia`),
  });
}

export function useAgreementPortalSettlement(
  id: string,
  period: string | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<AgreementPortalSettlement> {
  return useQuery({
    queryKey: agreementKeys.portalSettlement(id, period),
    enabled: (options?.enabled ?? true) && Boolean(id),
    queryFn: () =>
      api.get<AgreementPortalSettlement>(
        `/agreements/${id}/portal-billing/settlement${buildQuery({ period })}`,
      ),
  });
}

export function useSetAgreementSettlementState(
  id: string,
): UseMutationResult<AgreementPortalSettlement, unknown, SetAgreementSettlementStateInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetAgreementSettlementStateInput) =>
      api.post<AgreementPortalSettlement>(
        `/agreements/${id}/portal-billing/settlement/state`,
        input,
      ),
    onSuccess: (settlement) => {
      queryClient.setQueryData(
        agreementKeys.portalSettlement(id, settlement.period),
        settlement,
      );
      void queryClient.invalidateQueries({ queryKey: ['agreements', id, 'portal-settlement'] });
    },
  });
}

export function useAgreementPortalConnectBooster(
  id: string,
  options?: { enabled?: boolean },
): UseQueryResult<AgreementPortalConnectBoosterData> {
  return useQuery({
    queryKey: agreementKeys.portalConnectBooster(id),
    enabled: (options?.enabled ?? true) && Boolean(id),
    queryFn: () =>
      api.get<AgreementPortalConnectBoosterData>(
        `/agreements/${id}/portal-billing/connectbooster`,
      ),
  });
}

export function useCreateAgreement(): UseMutationResult<Agreement, unknown, CreateAgreementInput> {  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgreementInput) => api.post<Agreement>('/agreements', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.all });
    },
  });
}

export interface UpdateAgreementArgs {
  id: string;
  input: UpdateAgreementInput;
}

/**
 * Creates an active pay-as-you-go (tickets) agreement for a customer starting the first of
 * the current month and makes it that customer's default support agreement. Invalidates the
 * customer caches too, since `defaultSupportAgreementId` changed.
 */
export function useQuickAddPayAsYouGo(): UseMutationResult<
  Agreement,
  unknown,
  QuickAddPayAsYouGoInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: QuickAddPayAsYouGoInput) =>
      api.post<Agreement>('/agreements/quick-pay-as-you-go', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useUpdateAgreement(): UseMutationResult<Agreement, unknown, UpdateAgreementArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateAgreementArgs) =>
      api.patch<Agreement>(`/agreements/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.all });
    },
  });
}

export function useDeleteAgreement(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/agreements/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.all });
    },
  });
}

export function useRegenerateAgreementPublicLink(): UseMutationResult<Agreement, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Agreement>(`/agreements/${id}/public-link/regenerate`),
    onSuccess: (agreement) => {
      queryClient.setQueryData(agreementKeys.detail(agreement.id), agreement);
      void queryClient.invalidateQueries({ queryKey: agreementKeys.all });
    },
  });
}

export function useDisableAgreementPublicLink(): UseMutationResult<Agreement, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Agreement>(`/agreements/${id}/public-link/disable`),
    onSuccess: (agreement) => {
      queryClient.setQueryData(agreementKeys.detail(agreement.id), agreement);
      void queryClient.invalidateQueries({ queryKey: agreementKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Agreement lines
// ---------------------------------------------------------------------------

export function useAgreementLines(agreementId: string): UseQueryResult<AgreementLine[]> {
  return useQuery({
    queryKey: agreementKeys.lines(agreementId),
    enabled: Boolean(agreementId),
    queryFn: () => api.get<AgreementLine[]>(`/agreements/${agreementId}/lines`),
  });
}

export interface CreateAgreementLineArgs {
  agreementId: string;
  input: CreateAgreementLineInput;
}

export function useCreateAgreementLine(): UseMutationResult<
  AgreementLine,
  unknown,
  CreateAgreementLineArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, input }: CreateAgreementLineArgs) =>
      api.post<AgreementLine>(`/agreements/${agreementId}/lines`, input),
    onSuccess: (_data, { agreementId }) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.lines(agreementId) });
      void queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
    },
  });
}

export interface SeedAgreementLinesArgs {
  agreementId: string;
  opportunityId: string;
}

export interface ReorderAgreementLinesArgs {
  agreementId: string;
  orderedIds: string[];
}

/** Reorder an agreement's lines; the invoice emits its subscription lines in this order. */
export function useReorderAgreementLines(): UseMutationResult<
  AgreementLine[],
  unknown,
  ReorderAgreementLinesArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, orderedIds }: ReorderAgreementLinesArgs) =>
      api.patch<AgreementLine[]>(`/agreements/${agreementId}/lines/reorder`, { orderedIds }),
    onSuccess: (_data, { agreementId }) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.lines(agreementId) });
      void queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
    },
  });
}

export function useSeedAgreementLinesFromOpportunity(): UseMutationResult<
  AgreementLine[],
  unknown,
  SeedAgreementLinesArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, opportunityId }: SeedAgreementLinesArgs) =>
      api.post<AgreementLine[]>(
        `/agreements/${agreementId}/lines/seed-from-opportunity/${opportunityId}`,
      ),
    onSuccess: (_data, { agreementId }) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.lines(agreementId) });
      void queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
    },
  });
}

/** Adds the standard Catalyst+ product lines to a managed services agreement. */
export function useAddCatPlusLines(): UseMutationResult<AddCatPlusLinesResult, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agreementId: string) =>
      api.post<AddCatPlusLinesResult>(`/agreements/${agreementId}/lines/add-cat-plus`),
    onSuccess: (_data, agreementId) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.lines(agreementId) });
      void queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
    },
  });
}

export interface UpdateAgreementLineArgs {
  agreementId: string;
  lineId: string;
  input: UpdateAgreementLineInput;
}

export function useUpdateAgreementLine(): UseMutationResult<
  AgreementLine,
  unknown,
  UpdateAgreementLineArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, lineId, input }: UpdateAgreementLineArgs) =>
      api.patch<AgreementLine>(`/agreements/${agreementId}/lines/${lineId}`, input),
    onSuccess: (_data, { agreementId }) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.lines(agreementId) });
      void queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
    },
  });
}

export interface DeleteAgreementLineArgs {
  agreementId: string;
  lineId: string;
}

export function useDeleteAgreementLine(): UseMutationResult<
  void,
  unknown,
  DeleteAgreementLineArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, lineId }: DeleteAgreementLineArgs) =>
      api.delete(`/agreements/${agreementId}/lines/${lineId}`),
    onSuccess: (_data, { agreementId }) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.lines(agreementId) });
      void queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Agreement service-category rate overrides
// ---------------------------------------------------------------------------

export function useAgreementRates(
  agreementId: string,
): UseQueryResult<PaginatedResult<AgreementServiceCategoryRate>> {
  return useQuery({
    queryKey: agreementKeys.rates(agreementId),
    enabled: Boolean(agreementId),
    queryFn: () =>
      api.get<PaginatedResult<AgreementServiceCategoryRate>>(
        `/agreements/${agreementId}/service-category-rates${buildQuery({
          page: 1,
          pageSize: 200,
        })}`,
      ),
  });
}

export interface CreateAgreementRateArgs {
  agreementId: string;
  input: CreateAgreementServiceCategoryRateInput;
}

export function useCreateAgreementRate(): UseMutationResult<
  AgreementServiceCategoryRate,
  unknown,
  CreateAgreementRateArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, input }: CreateAgreementRateArgs) =>
      api.post<AgreementServiceCategoryRate>(
        `/agreements/${agreementId}/service-category-rates`,
        input,
      ),
    onSuccess: (_data, { agreementId }) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.rates(agreementId) });
    },
  });
}

export interface UpdateAgreementRateArgs {
  agreementId: string;
  id: string;
  input: UpdateAgreementServiceCategoryRateInput;
}

export function useUpdateAgreementRate(): UseMutationResult<
  AgreementServiceCategoryRate,
  unknown,
  UpdateAgreementRateArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, id, input }: UpdateAgreementRateArgs) =>
      api.patch<AgreementServiceCategoryRate>(
        `/agreements/${agreementId}/service-category-rates/${id}`,
        input,
      ),
    onSuccess: (_data, { agreementId }) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.rates(agreementId) });
    },
  });
}

export interface DeleteAgreementRateArgs {
  agreementId: string;
  id: string;
}

export function useDeleteAgreementRate(): UseMutationResult<
  void,
  unknown,
  DeleteAgreementRateArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, id }: DeleteAgreementRateArgs) =>
      api.delete(`/agreements/${agreementId}/service-category-rates/${id}`),
    onSuccess: (_data, { agreementId }) => {
      void queryClient.invalidateQueries({ queryKey: agreementKeys.rates(agreementId) });
    },
  });
}
